<template>
  <div class="view-wrapper initiatives-view" :class="{ 'has-year-end-margin': hasItemNearDateRangeEnd }">
    <ViewHeader :title="$store.getters.currentPlan.name" :isFullViewport="false">
      <template #actions>
        <Button
          label="Add Tactic"
          icon="pi pi-plus-circle"
          class="p-mr-2"
          @click="onAddTactic"
        />
        <Button
          label="Filters"
          icon="pi pi-sliders-h"
          class="p-button-outlined"
          @click="showTacticFiltersView = true"
          :badge="$store.getters.activeFiltersCount"
          badgeClass="p-badge-info"
        />
        <template v-if="isLeadPlan || isNestedPlan">
          <LeadNestedFilters />
        </template>
      </template>
      <template>
        <div class="p-d-flex p-justify-between p-ai-end">
          <div class="p-d-flex">
            <div>
              <DateRangePicker
                :isMonthRangeView="true"
              />
            </div>
            <p class="date-range-instruction" style="font-size: 1rem; color: #666; margin: 2.8rem 0.5rem 0 1rem;">Date range is limited to one year.</p>
          </div>

          <Button
            ref="downloadPDFBtn"
            label="PDF"
            icon="pi pi-download"
            class="p-button-outlined download-pdf-btn"
            @click="onDownloadPDFClick"
            v-tooltip.left="'Click to open browser print menu. Select &quot;Background graphics&quot; to show colored boxes. Generate and download PDF.'"
          />
        </div>
      </template>
    </ViewHeader>
    <ViewMain>
      <div class="flow-container" :class="{'is-lead-plan': isLeadPlan}">
        <div class="flow-dates-row p-d-flex">
          <div v-for="(month, index) in currentFlowDisplayMonths" :key="month.index" class="cell"
            :style="{ width: (month.numDays * dayWidth).toString() + '%' }">
            <div class="month-year">
              <span class="month">{{ month.name }}</span>
              <span class="year" v-if="index == 0 || month.month == 0">{{
                month.year
              }}</span>
            </div>
          </div>
        </div>

        <Accordion :multiple="true" :activeIndex="accordionActiveIndicies" @tab-close="onAccordionTabClose" @tab-open="onAccordionTabOpen" class="widgets-accordion flow-accordion">
          <AccordionTab v-show="filteredSubscriptionEvents.length" header="Calendars of Interest">
            <template #default v-if="filteredSubscriptionEvents.length">
              <div class="flow-events-row p-d-flex">
                <div class="events-container">
                  <CalendarFlowRow :planningItems="filteredSubscriptionEvents" :dayWidth="dayWidth"
                    :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="false"
                    :isSubscription="true" :titleOverflowBufferDays="initiativeTitleOverflowBufferDays"
                    :buttonTitleMaxWidthNumDays="subscriptionButtonTitleMaxWidthNumDays"
                    :disableTitleTruncation="true" :instanceIndex="0" />
                </div>
              </div>
            </template>
          </AccordionTab>
          <template v-for="(initiativeType, initiativeTypeIndex) in initiativesByType">
            <template>
              <AccordionTab
                v-show="initiativeType.initiatives.length"
                :key="initiativeType.id.intID"
              >
                <template #header>
                  <div class="p-d-flex p-jc-between p-ai-center" style="width: 100%;">
                    <div class="p-accordion-header-text">{{ initiativeType.name }}</div>
                  </div>
                </template>
                <template #default>
                  <div class="flow-events-row p-d-flex">
                    <div class="events-container">
                      <CalendarFlowRow :planningItems="initiativeType.initiatives" :dayWidth="dayWidth"
                        :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="false"
                        :shouldUsePlanUiColor="isLeadPlan || isNestedPlan" :titleOverflowBufferDays="initiativeTitleOverflowBufferDays"
                        :buttonTitleMaxWidthNumDays="initiativeButtonTitleMaxWidthNumDays"
                        :disableTitleTruncation="true" :instanceIndex="initiativeTypeIndex + 1"
                        @showTactic="showInitiative" @showContextMenu="showContextMenu"
                        @showPlanningItemSummaryOverlay="showTacticSummaryOverlay"
                        @hidePlanningItemSummaryOverlay="hideTacticSummaryOverlay" />
                    </div>
                  </div>
                </template>
              </AccordionTab>
            </template>
          </template>
          <AccordionTab v-show="filteredPaidChannelTactics.length" header="Long-Running Tactics">
            <template #default>
              <div class="flow-events-row p-d-flex">
                <div class="events-container">
                  <CalendarFlowRow :planningItems="filteredPaidChannelTactics" :dayWidth="dayWidth"
                    :millisecondsPerDay="millisecondsPerDay" :dateRange="dateRange" :shouldShowChannelAndTypeNames="true"
                    :shouldUseChannelUiColor="true" :shouldUsePlanUiColor="false"
                    :titleOverflowBufferDays="initiativeTitleOverflowBufferDays"
                    :buttonTitleMaxWidthNumDays="initiativeButtonTitleMaxWidthNumDays"
                    :disableTitleTruncation="true" :instanceIndex="initiativesByType.length + 1"
                    @showTactic="showTactic" @showContextMenu="showContextMenu"
                    @showPlanningItemSummaryOverlay="showTacticSummaryOverlay"
                    @hidePlanningItemSummaryOverlay="hideTacticSummaryOverlay" />
                </div>
              </div>
            </template>
          </AccordionTab>
        </Accordion>

        <div class="copyright-container">
          <p class="copyright">Copyright &copy; {{ currentYear }} Annum. All rights reserved. United States Patent Number 12,079,779.</p>
        </div>

        <ContextMenu ref="menu" :model="contextMenuItems" appendTo="body" />

        <OverlayPanel ref="planningItemSummaryOverlay" class="planning-item-summary-overlay" appendTo="body">
          <PlanningItemSummary :planningItemId="currentSummaryPlanningItemId" :instanceStartDate="currentSummaryInstanceStartDate" :instanceEndDate="currentSummaryInstanceEndDate" />
        </OverlayPanel>

        <Dialog header="Distribute Tactic to Other Plans" :visible.sync="shouldShowDistributeTacticModal" :modal="true">
          <DistributeTacticModal :tacticId="rightClickedTacticId" @hide="shouldShowDistributeTacticModal = false" />
        </Dialog>

        <Dialog header="Create Nested Variants" :visible.sync="shouldShowDistributeInitiativeModal" :modal="true">
          <DistributeInitiativeModal :initiativeId="rightClickedTacticId"
            @hide="shouldShowDistributeInitiativeModal = false" />
        </Dialog>

      </div>

      <TacticFilters
        :showHide="showTacticFiltersView"
        @shouldHide="showTacticFiltersView = false"
      />
    </ViewMain>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Button from 'primevue/button'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Tactic from '@/models/Tactic'
import FlowDisplayMonth from '@/models/FlowDisplayMonth'
import CalendarFlowRow from '@/components/CalendarFlowRow.vue'
import Subscription from '@/models/Subscription'
import ID from '@/models/ID'
import ContextMenuItem from '@/models/ContextMenuItem'
import ContextMenu from 'primevue/contextmenu'
import PlanningItemSummary from '@/views/PlanningItemSummary.vue'
import Dialog from 'primevue/dialog'
import DistributeInitiativeModal from '@/components/DistributeInitiativeModal.vue'
import InitiativeType from '@/models/InitiativeType'
import PlanningItem from '@/models/InterfacePlanningItem'
import TacticDisplayMixin from '@/mixins/TacticDisplayMixin'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Role from '@/models/Role'
import DateRangePicker from '@/components/DateRangePicker.vue'
import TacticFilters from '@/components/TacticFilters.vue'
import Tooltip from 'primevue/tooltip'
import LeadNestedFilters from '@/components/LeadNestedFilters.vue'

Vue.component('Dialog', Dialog)
Vue.component('ContextMenu', ContextMenu)
Vue.component('Button', Button)
Vue.component('Accordion', Accordion)
Vue.component('AccordionTab', AccordionTab)

export default Vue.extend({
  name: 'InitiativesFlow',
  mixins: [TacticDisplayMixin],
  components: {
    ViewHeader,
    ViewMain,
    DateRangePicker,
    TacticFilters,
    CalendarFlowRow,
    PlanningItemSummary,
    DistributeInitiativeModal,
    LeadNestedFilters,
  },
  directives: {
    tooltip: Tooltip,
  },
  data: () => {
    return {
      yearEndMarginThresholdPercent: 0.95 as number, // Items starting after this percentage of the timeline are considered "near the end"
      millisecondsPerDay: (1000 * 60 * 60 * 24) as number,
      rightClickedTacticId: {} as ID,
      rightClickedTacticIsInitiative: false as boolean,
      rightClickedTacticIsInitiativeCustomization: false as boolean,
      rightClickedTacticIsLead: false as boolean,
      rightClickedTacticHasCustomizationForCurrentPlan: false as boolean,
      rightClickedTacticHasCustomizationsForAllNestedPlans: false as boolean,
      currentSummaryPlanningItemId: {} as ID,
      currentSummaryInstanceStartDate: null as Date | null,
      currentSummaryInstanceEndDate: null as Date | null,
      currentSummaryTimeout: 0,
      isDisplayingSummaryPlanningItem: false as boolean,
      shouldShowDistributeTacticModal: false as boolean,
      shouldShowDistributeInitiativeModal: false as boolean,
      showTacticFiltersView: false as boolean,
      tacticsUpdateIterator: 0 as number,
      tacticsUpdateDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      collapsedTabIds: [] as string[],
    }
  },
  computed: {
    currentYear(): number {
      return new Date().getFullYear()
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    isNestedPlan(): boolean {
      return this.$store.getters.currentPlan.parentId.intID !== 0 ? true : false
    },
    dateRange(): Date[] {
      return this.$store.getters.monthRangeViewsDateRange
    },
    dayWidth(): number {
      if (!this.dateRange[1] || this.dateRange[0] == this.dateRange[1]) {
        return 100
      }
      const utc1 = Date.UTC(
        this.dateRange[0].getFullYear(),
        this.dateRange[0].getMonth(),
        this.dateRange[0].getDate()
      )
      const utc2 = Date.UTC(
        this.dateRange[1].getFullYear(),
        this.dateRange[1].getMonth(),
        this.dateRange[1].getDate()
      )
      // Calculate milliseconds between start and end dates and add one day so last day of range is displayed
      return 100 / ((utc2 - utc1 + this.millisecondsPerDay) / this.millisecondsPerDay)
    },
    initiativeTitleOverflowBufferDays(): number {
      if (!this.dateRange[0] || !this.dateRange[1]) return 14
      const totalMs = this.dateRange[1].getTime() - this.dateRange[0].getTime()
      const totalDays = totalMs / this.millisecondsPerDay
      // Buffer scales with date range: ~20 days for a 365-day range, min 10
      return Math.min(20, Math.max(10, Math.ceil(totalDays * 20 / 365)))
    },
    initiativeButtonTitleMaxWidthNumDays(): number {
      if (!this.dateRange[0] || !this.dateRange[1]) return 6
      const totalMs = this.dateRange[1].getTime() - this.dateRange[0].getTime()
      const totalDays = totalMs / this.millisecondsPerDay
      // Minimum title footprint scales with date range: ~12 days for a 365-day range, min 6
      return Math.min(20, Math.max(6, Math.ceil(totalDays / 30)))
    },
    // Subscription events (calendars of interest) are single-day items with potentially long names
    // like "Independence Day (Observed)". When disableTitleTruncation is true, this value is used
    // directly as the subrow collision buffer, so it must be large enough to approximate the
    // visual width of the longest title text. totalDays / 5 gives ~70 days for a year view,
    // which covers most holiday names at typical font sizes. No upper cap is applied since
    // under-spacing causes visual collisions between items on the same subrow.
    subscriptionButtonTitleMaxWidthNumDays(): number {
      if (!this.dateRange[0] || !this.dateRange[1]) return 30
      const totalMs = this.dateRange[1].getTime() - this.dateRange[0].getTime()
      const totalDays = totalMs / this.millisecondsPerDay
      return Math.max(14, Math.ceil(totalDays / 5))
    },
    currentFlowDisplayMonths(): FlowDisplayMonth[] {
      const returnArray = [] as FlowDisplayMonth[]
      if (!this.dateRange[0] || !this.dateRange[1]) return returnArray

      const tempDate = new Date(this.dateRange[0].toString())
      let numMonths =
        (this.dateRange[1].getFullYear() - this.dateRange[0].getFullYear()) * 12
      numMonths -= this.dateRange[0].getMonth()
      numMonths += this.dateRange[1].getMonth()

      for (let i = 0; i <= numMonths; i += 1) {
        if (i > 0) {
          tempDate.setMonth(tempDate.getMonth() + 1)
        }
        returnArray.push(
          new FlowDisplayMonth(
            i,
            tempDate.toLocaleString('default', { month: 'short' }),
            i == 0 ? this.dateRange[0].getDate() : 1,
            i == numMonths ? this.dateRange[1].getDate() : new Date(tempDate.getFullYear(), tempDate.getMonth() + 1, 0).getDate(),
            tempDate.getMonth(),
            tempDate.getFullYear(),
            this.$store.getters.currentPlan.weekStartDay
          )
        )
      }
      return returnArray
    },
    filteredSubscriptionEvents(): Tactic[] {
      const returnArray = [] as Tactic[]
      this.$store.getters.selectedSubscriptions.forEach((subscription) => {
        const subscriptionCopy = { ...subscription }

        // Add recurrence clones
        this.$store.dispatch('addRecurringEventClones', subscriptionCopy)

        subscriptionCopy.events.forEach((tactic) => {
          if (
            (this as any).isTacticTouchingDateRange(tactic) &&
            !this.isEventDuplicate(tactic, subscriptionCopy)
          ) {
            returnArray.push(tactic)
          }
        })
      })
      return returnArray
    },
    initiativesByType(): InitiativeType[] {
      const useAll = (this as any).filterNoInitiative || (this as any).filterNoTag
      const sourceCategories = useAll
        ? this.$store.getters.allInitiativesInCategories
        : this.$store.getters.selectedInitiativesInCategories
      return sourceCategories.map((type: InitiativeType) => {
        const filteredInitiatives = type.initiatives.filter((planningItem: PlanningItem) =>
          !(this as any).isTacticInFilteredOutPlan(planningItem) &&
          (this as any).isTacticTouchingDateRange(planningItem)
        )
        filteredInitiatives.sort((a: PlanningItem, b: PlanningItem) => {
          if (a.isLead && !b.isLead) return -1
          if (b.isLead && !a.isLead) return 1
          return 0
        })
        return {
          ...type,
          initiatives: filteredInitiatives
        } as InitiativeType
      })
    },
    filteredPaidChannelTactics(): Tactic[] {
      // Access tacticsUpdateIterator to create a reactive dependency for forced re-computation
      this.tacticsUpdateIterator
      if(!this.$store.getters.showLongRunningTacticsInInitiativesView){
        return []
      }
      const returnArray = [] as Tactic[]
      // Using currentTacticTypes instead of selectedTacticTypes to ignore channel filter selections on InitiativesFlow view
      this.$store.getters.currentTacticTypes.forEach((type) => {
        const tacticsWithClonesForType = type.getTacticsWithClonesForDateRange(this.dateRange) as Tactic[]
        tacticsWithClonesForType.forEach((tactic) => {
          if (
            !(this as any).isTacticInFilteredOutPlan(tactic) &&
            !(this as any).isTacticRelatedToDeselectedTag(tactic) &&
            !(this as any).isTacticRelatedToDeselectedInitiative(tactic) &&
            (this as any).isTacticLongerThanThreshold(tactic, 21) &&
            (this as any).isTacticInPaidChannel(tactic) &&
            (this as any).isTacticTouchingDateRange(tactic)
          ) {
            returnArray.push(tactic)
          }
        })
      })
      return returnArray
    },
    initiativeTypesWithData(): InitiativeType[] {
      return this.initiativesByType.filter((type) => type.initiatives.length > 0)
    },
    collapsedTabsStorageKey(): string {
      return `annum-initiatives-collapsed-tabs-${this.$store.getters.currentPlan.id.intID}`
    },
    accordionActiveIndicies(): number[] {
      const indices: number[] = []
      let currentIndex = 0

      if (this.filteredSubscriptionEvents.length && !this.collapsedTabIds.includes('calendars-of-interest')) {
        indices.push(currentIndex)
      }
      currentIndex++

      this.initiativesByType.forEach((type) => {
        if (type.initiatives.length && !this.collapsedTabIds.includes(`initiative-type-${type.id.intID}`)) {
          indices.push(currentIndex)
        }
        currentIndex++
      })

      if (this.filteredPaidChannelTactics.length && !this.collapsedTabIds.includes('long-running-tactics')) {
        indices.push(currentIndex)
      }

      return indices
    },
    contextMenuItems(): ContextMenuItem[] {
      const returnArray = [] as ContextMenuItem[]
      if (this.shouldShowCopyContextMenuItem) {
        returnArray.push({
          label: 'Copy',
          icon: 'pi pi-fw pi-copy',
          command: this.openTacticDetailWithCopyId,
        })
      }
      if (this.shouldShowInitiativeCustomizationContextMenuItem) {
        returnArray.push({
          label: 'Create Nested Variant',
          icon: 'pi pi-fw pi-link',
          command: this.openInitiativeCustomizationDetailWithCopyId,
        })
      }
      if (this.shouldShowDistributeInitiativeContextMenuItem) {
        returnArray.push({
          label: 'Create Nested Variants',
          icon: 'pi pi-fw pi-link',
          command: this.createInitiativeVariantsInNestedPlans,
        })
      }
      return returnArray
    },
    shouldShowCopyContextMenuItem(): boolean {
      if (
        (this.isLeadPlan && !this.rightClickedTacticIsLead) ||
        (this.isNestedPlan && this.rightClickedTacticIsLead) ||
        this.rightClickedTacticIsInitiativeCustomization
      ) {
        return false
      }
      return true
    },
    shouldShowInitiativeCustomizationContextMenuItem(): boolean {
      return this.rightClickedTacticIsInitiative && this.rightClickedTacticIsLead && this.isNestedPlan && !this.rightClickedTacticHasCustomizationForCurrentPlan
    },
    shouldShowDistributeInitiativeContextMenuItem(): boolean {
      return this.rightClickedTacticIsInitiative && this.rightClickedTacticIsLead && this.isLeadPlan && !this.rightClickedTacticHasCustomizationsForAllNestedPlans
    },
    canUserAddTactics(): boolean {
      return this.$store.getters.getPermissionLevelForPlanId(this.$store.getters.currentPlan.id) <=
        Role.LEVEL_CONTRIBUTOR
        ? true
        : false
    },
    hasItemNearDateRangeEnd(): boolean {
      if (!this.dateRange[0] || !this.dateRange[1]) return false

      const dateRangeStart = this.dateRange[0].getTime()
      const dateRangeEnd = this.dateRange[1].getTime()
      const totalDuration = dateRangeEnd - dateRangeStart
      const thresholdPoint = dateRangeStart + (this.yearEndMarginThresholdPercent * totalDuration)

      // Check initiatives from all initiative types
      for (const initiativeType of this.initiativesByType) {
        for (const initiative of initiativeType.initiatives) {
          if (initiative.startDate) {
            const startTime = initiative.startDate.getTime()
            if (startTime >= thresholdPoint && startTime <= dateRangeEnd) {
              return true
            }
          }
        }
      }

      // Check long-running tactics
      for (const tactic of this.filteredPaidChannelTactics) {
        if (tactic.startDate) {
          const startTime = tactic.startDate.getTime()
          if (startTime >= thresholdPoint && startTime <= dateRangeEnd) {
            return true
          }
        }
      }

      return false
    },
  },
  watch: {
    '$store.getters.selectedSubscriptions': {
      deep: true,
      handler() {
        // Trigger date update with exact same date to trigger render in CalendarFlowRows
        setTimeout(() => {
          this.$store.dispatch('updateCurrentDateRange', {
            newDateRange: [
              this.$store.getters.currentDateRange[0],
              this.$store.getters.currentDateRange[1],
            ]
          })
        }, 100)
      },
    },
    collapsedTabIds(newVal: string[]) {
      localStorage.setItem(this.collapsedTabsStorageKey, JSON.stringify(newVal))
    },
    '$store.getters.currentDateRange': {
      deep: true,
      handler() {
        // Debounce re-computation to avoid multiple updates during tactics fetch
        if (this.tacticsUpdateDebounceTimer) {
          clearTimeout(this.tacticsUpdateDebounceTimer)
        }
        this.tacticsUpdateDebounceTimer = setTimeout(() => {
          this.tacticsUpdateIterator += 1
          this.tacticsUpdateDebounceTimer = null
        }, 150)
      },
    },
  },
  created() {
    const saved = localStorage.getItem(this.collapsedTabsStorageKey)
    if (saved) {
      try {
        this.collapsedTabIds = JSON.parse(saved)
      } catch {
        this.collapsedTabIds = []
      }
    }
  },
  mounted: function () {
    this.$store.dispatch('updateCurrentDateRange', { newDateRange: this.$store.getters.monthRangeViewsDateRange })
  },
  beforeDestroy() {
    window.clearTimeout(this.currentSummaryTimeout)
    if (this.tacticsUpdateDebounceTimer) {
      clearTimeout(this.tacticsUpdateDebounceTimer)
    }
  },
  methods: {
    getTabStableId(index: number): string {
      if (index === 0) return 'calendars-of-interest'
      const typeIndex = index - 1
      if (typeIndex < this.initiativesByType.length) {
        return `initiative-type-${this.initiativesByType[typeIndex].id.intID}`
      }
      return 'long-running-tactics'
    },
    onAccordionTabClose(e: { originalEvent: Event; index: number }) {
      const stableId = this.getTabStableId(e.index)
      if (!this.collapsedTabIds.includes(stableId)) {
        this.collapsedTabIds = [...this.collapsedTabIds, stableId]
      }
    },
    onAccordionTabOpen(e: { originalEvent: Event; index: number }) {
      const stableId = this.getTabStableId(e.index)
      this.collapsedTabIds = this.collapsedTabIds.filter(id => id !== stableId)
    },
    onDownloadPDFClick() {
      // Hide tooltip
      ((this.$refs.downloadPDFBtn as Vue)?.$el as HTMLElement)?.blur()

      // Wait until next tick to hide sidebar
      this.$nextTick(() => {
        this.$store.commit('updateIsPrintDialogOpen', true)

        // Wait to show print dialog
        window.setTimeout(() => {
          window.print()
        }, 100)
      })
    },
    onAddTactic() {
      if (this.canUserAddTactics) {
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/tactic/0`
        })
      } else {
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.warn,
          summary: 'Viewers do not have the ability to add to or edit the Plan.',
          life: 3000,
        })
      }
    },
    isEventDuplicate(event: Tactic, subscription: Subscription): boolean {
      let returnValue = false
      const duplicateMappingsForTactic = subscription.duplicateEventsMap.filter(
        (duplicateMapping) => duplicateMapping.eventId == event.id.intID
      )
      // Only check for duplicates if there is a duplicate for the provided event
      if (duplicateMappingsForTactic.length) {
        duplicateMappingsForTactic.forEach((duplicateMapping) => {
          // Check if subscription with duplicate is visible
          const selectedSubscriptionWithDuplicate =
            this.$store.getters.selectedSubscriptions.find(
              (selectedSubscription) =>
                selectedSubscription.id.intID ==
                duplicateMapping.duplicateSubscriptionId
            )
          if (selectedSubscriptionWithDuplicate) {
            // Check if subscription with duplicate has a lower ID value than the provided subscription - event with lowest subscription ID will be shown
            if (
              duplicateMapping.duplicateSubscriptionId <
              duplicateMapping.subscriptionId
            ) {
              returnValue = true
            }
          }
        })
      }
      return returnValue
    },
    showTactic(tactic: Tactic) {
      const thisTacticIntID = tactic.originalTacticID ? tactic.originalTacticID.intID : tactic.id.intID
      this.$router.push({
        path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/tactic/${thisTacticIntID}`,
      })
    },
    showInitiative(planningItem: PlanningItem) {
      if (planningItem.isInitiativeCustomization) {
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/initiative/${planningItem.initiativeId?.intID}/nested-variant/${planningItem.id.intID}`,
        })
      } else {
        this.$router.push({
          path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/initiative/${planningItem.id.intID}`,
        })
      }
    },
    showContextMenu({ e, planningItem }) {
      if (planningItem.isRecurringEventClone) {
        this.rightClickedTacticId = planningItem.originalTacticID
      } else {
        this.rightClickedTacticId = planningItem.id
      }
      this.rightClickedTacticIsInitiative = planningItem.isInitiative
      this.rightClickedTacticIsInitiativeCustomization = planningItem.isInitiativeCustomization
      this.rightClickedTacticIsLead = planningItem.isLead
      this.rightClickedTacticHasCustomizationForCurrentPlan = planningItem.initiativeCustomizations?.filter((customization) => customization.planId.intID === this.$store.getters.currentPlan.id.intID).length > 0
      this.rightClickedTacticHasCustomizationsForAllNestedPlans = this.$store.getters.currentPlan.plans?.every((nestedPlanId) => planningItem.initiativeCustomizations?.some((customization) => customization.planId.intID === nestedPlanId.intID))

      if (this.contextMenuItems.length) {
        (this.$refs.menu as Vue & { show: (e) => boolean }).show(e)
      }
    },
    openTacticDetailWithCopyId() {
      this.$router.push({
        path:
          `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/${this.rightClickedTacticIsInitiative ? 'initiative' : 'tactic'}/0?copyof=${this.rightClickedTacticId.intID}`
      })
    },
    openInitiativeCustomizationDetailWithCopyId() {
      this.$router.push({
        path:
          `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$route.params.planId}/initiative/${this.rightClickedTacticId.intID}/nested-variant/0`
      })
    },
    createInitiativeVariantsInNestedPlans() {
      this.shouldShowDistributeInitiativeModal = true
    },
    showTacticSummaryOverlay(info) {
      // Show planning item summary overlay with delay on first hover, no delay on subsequent hovers
      const planningItemSummaryDisplayDelay = this.isDisplayingSummaryPlanningItem ? 0 : 2000
      this.currentSummaryPlanningItemId = info.planningItem.id
      if (info.planningItem.originalTacticID) {
        this.currentSummaryPlanningItemId = info.planningItem.originalTacticID
        this.currentSummaryInstanceStartDate = info.planningItem.startDate
        this.currentSummaryInstanceEndDate = info.planningItem.endDate
      } else {
        this.currentSummaryInstanceStartDate = null
        this.currentSummaryInstanceEndDate = null
      }
      window.clearTimeout(this.currentSummaryTimeout)
      this.currentSummaryTimeout = window.setTimeout(() => {
        if (!this.$refs.planningItemSummaryOverlay) return
        // The hover target is a raw DOM node captured at mouseenter time. If it re-renders
        // and detaches before this delayed timeout fires, PrimeVue's OverlayPanel measures a
        // detached node as all zeros and renders pinned at the viewport's upper-left corner.
        if (!document.contains(info.event.target as Node)) return
        this.isDisplayingSummaryPlanningItem = true
        // @ts-expect-error
        this.$refs.planningItemSummaryOverlay.show({ ...info.event, currentTarget: info.event.target })
      }, planningItemSummaryDisplayDelay)
    },
    hideTacticSummaryOverlay() {
      // Hide planning item details overlay
      if (!this.$refs.planningItemSummaryOverlay) return
      // @ts-expect-error
      this.$refs.planningItemSummaryOverlay.hide()

      // Reset timeout after delay
      window.clearTimeout(this.currentSummaryTimeout)
      this.currentSummaryTimeout = window.setTimeout(() => {
        this.isDisplayingSummaryPlanningItem = false
        this.currentSummaryPlanningItemId = {} as ID
        this.currentSummaryInstanceStartDate = null
        this.currentSummaryInstanceEndDate = null
        window.clearTimeout(this.currentSummaryTimeout)
      }, 3000)
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.global-nav-collapsed {
  .initiatives-view {
    .view-main:has(.flow-container) {
      // Width with nav closed, subtracted px value accounts for right margin from edge of viewport
      width: calc(100% - 30px);
    }
  }
}

.initiatives-view {
  @-moz-document url-prefix() {

    // Cover for padding in ViewMain - allows sticky positioning in Flow view without revealing content while scrolling
    // This targets only Firefox because it does not support :has() solution below
    .flow-container {
      &:before {
        content: '';
        position: fixed;
        display: block;
        top: 0;
        left: $globalNavWidth;
        bottom: 0;
        width: 1.6rem;
        background-color: #fff;
        z-index: 2;
        transition: left 0.3s;
      }
    }

    .global-nav-collapsed {
      .flow-container {
        &:before {
          left: $navCollapseButtonWidth;
        }
      }
    }
  }

  &.has-year-end-margin {
    .view-main {
      .flow-dates-row,
      .flow-accordion {
        width: calc(100% - 76px);
      }
    } 
  }

  .view-main {
    .flow-accordion {
      .p-accordion-tab {
        .p-toggleable-content {
          clip-path: none;

          .p-accordion-content {
            .flow-events-row {
              .events-container {
                overflow: visible;

                .tactics-row {
                  overflow: visible;
                  
                  .tactic-button {
                    &.is-nested {
                      .bkg {
                        opacity: 0.6;
                      }
                    }
                    &.is-lead {
                      .bkg {
                        opacity: 1;
                      }
                    }
                    &.initiatives {
                      &.is-nested {
                        &.uses-initiatives-color {
                          .bkg {
                            opacity: 1;
                          }
                        }
                      }
                    }
                    // This section section restores opacity changes for individual plans. There was direction on 04.14.26 to reduce this to 60% - JV 04.14.26
                    // &:not(.is-lead, .is-nested){
                    //   .bkg {
                    //     opacity: 1;
                    //   }
                    // }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .view-main:has(.flow-container) {
    padding-left: 0;
    padding-right: 30px;
    margin-left: 15px;
    // Width with nav open, spacing from right viewport edge is managed by calculated width on .flow-dates-row and .flow-accordion
    width: calc(85%);
  }

  .global-nav-collapsed .view-main:has(.flow-container) {
    left: $navCollapseButtonWidth;
    width: calc(100% - $navCollapseButtonWidth - 15px);
  }

  .flow-container {
    &:not(.show-budget) {
      .channel-meta-header-container {
        width: 2.4rem;
        height: 0.1rem;
        background: transparent !important;
      }
    }
  }

  //Accordion overrides
  .flow-accordion {
    position: relative;

    .p-accordion-tab {
      margin-bottom: 0;
    }

    .p-accordion-header {
      position: relative;
      width: 100%;

      &:after {
        display: none;
      }

      &.p-disabled {
        opacity: 1;

        .p-accordion-header-link {
          .p-accordion-toggle-icon {
            display: none;
          }

          h2 {
            left: 0.8rem;
          }
        }
      }

      .p-accordion-header-link,
      &:not(.p-highlight):not(.p-disabled):hover .p-accordion-header-link,
      &:not(.p-disabled).p-highlight .p-accordion-header-link {
        background: #f6f6f6;
        border: none;
        color: $black;
        z-index: 2;
        position: static;
        padding: 0 0 0 0.5rem;
        height: 3rem;

        &:hover {
          background: #f6f6f6;
          border: none;
          color: $black;
        }
      }

      h2,
      .p-accordion-header-text,
      .p-accordion-toggle-icon {
        position: sticky;
        left: 2.4rem;
        margin-top: 0;
      }

      h2 {
        margin: 0;
        font-size: 1.1rem;
        color: #333333;
        @include font-normal;

        .name {
          display: inline-block;
        }

        &:before {
          // Line to match border in accordion contents
          content: "";
          display: block;
          width: 0.1rem;
          height: 3rem;
          background: #999;
          position: absolute;
          top: -0.8rem;
          left: 15.5rem;
        }
      }

      .p-accordion-header-text {
        left: 2.5rem;
      }

      .p-accordion-toggle-icon {
        left: 0.8rem;
      }
    }

    .p-accordion-content {
      padding: 0;
      border: none;

      &:has(.touching-range-end),
      &:has(.spanning-range-end) {
        overflow: visible;
      }
    }

    .p-toggleable-content {
      clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);

      &:has(.touching-range-end),
      &:has(.spanning-range-end) {
        clip-path: none;
      }
    }

    .events-container {
      border-right: 0;
    }
  }

  //Flow rows
  .flow-dates-row,
  .flow-events-row {
    &>.title {
      min-width: 18rem;
      min-height: 2rem;
      margin-right: 1rem;
    }
  }

  .flow-dates-row {
    position: sticky !important;
    top: 0;
    height: 2rem;
    background-color: #efefef;
    z-index: 1;
    width: 100%;

    .title {
      position: sticky;
      left: 0;
      top: 0;
      z-index: 3;
      background-color: #efefef;
    }

    .cell {
      position: relative;
      
      &:not(:first-child) {
        border-left: 0.1rem solid #999999;
      }
      .month,
      .year {
        font-size: 1rem;
        line-height: 1.2rem;
        color: #707070;
        text-transform: uppercase;
      }

      .month-year {
        position: absolute;
        top: 0.2rem;
        left: 0.4rem;
      }

      .year {
        padding-left: 0.25rem;
      }
    }
  }

  .flow-events-row {
    min-height: 2.8rem;

    &:has(.touching-range-end),
    &:has(.spanning-range-end) {
      overflow: visible;
    }

    &>.title {
      position: sticky;
      left: 0;
      z-index: 2;

      h2,
      h3 {
        position: absolute;
        height: 100%;
        min-width: inherit;
        max-width: 12rem;
        z-index: 1;
        margin: 0;
        padding: 0.5rem 0 0.5rem 2.5rem;
        border-right: 0.1rem solid #999999;
        background: #fff;
        @include font-normal;
        font-size: 1.1rem;
        color: #333333;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        cursor: default;
        pointer-events: none;
      }
    }

    .events-container {
      position: relative;
      width: 100%;
      overflow: hidden;

      &:has(.touching-range-end),
      &:has(.spanning-range-end) {
        overflow: visible;
      }
    }
  }
}
</style>
